import {
  type PayoutSettingsResponse,
  payoutSettingsResponseSchema,
} from "@clipboard-health/contract-payment-service";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { type ExternalAccountDetailsAttributes } from "./schema";

export type GetPayoutSettingsResponse = PayoutSettingsResponse;

export function getPayoutSettingsUrl(workerId: string) {
  return `${environmentConfig.REACT_APP_PAYMENTS_API_URL}/accounts/${workerId}/payout-settings`;
}

export function getGetPayoutSettingsQueryKey(workerId: string) {
  return [getPayoutSettingsUrl(workerId)];
}

export interface UseGetPayoutSettingsResponse {
  attributes: GetPayoutSettingsResponse["data"]["attributes"];
  meta: GetPayoutSettingsResponse["data"]["meta"];
  destinationDetails: ExternalAccountDetailsAttributes | undefined;
}

export function mapPayoutSettingsToResponseWithDestination(
  response: GetPayoutSettingsResponse
): UseGetPayoutSettingsResponse {
  const { attributes, meta } = response.data;
  const destinationStripeId = attributes.preferredExternalAccountStripeId;
  const destinationDetails = response.included.find(
    (included) => included.id === destinationStripeId
  )?.attributes;

  return {
    attributes,
    meta,
    destinationDetails,
  };
}

export function useGetPayoutSettings(): UseQueryResult<UseGetPayoutSettingsResponse> {
  const worker = useDefinedWorker();
  const url = getPayoutSettingsUrl(worker.userId);

  return useQuery({
    queryKey: getGetPayoutSettingsQueryKey(worker.userId),
    queryFn: async () => {
      const response = await get({
        url,
        responseSchema: payoutSettingsResponseSchema,
      });

      return mapPayoutSettingsToResponseWithDestination(response.data);
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PAYOUT_SETTINGS_GET_SETTINGS_FAILURE,
    },
  });
}
